import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import {
  Card,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from '@mui/material';
import { useNavigate, useSearchParams } from "react-router-dom";

import type { Workout, WorkoutsListEntry } from "../api/workout-service";
import WorkoutService from "../api/workout-service";
import type { WorkoutDefinition } from "../api/workout-definitions-service";
import { WorkoutDefinitionsService } from "../api/workout-definitions-service";
import type { UserListHeadCell } from '../sections/@dashboard/user/UserListHead';

import Scrollbar from '../components/scrollbar';
import { UserListHead } from '../sections/@dashboard/user';
import ENV from "../env";
import Avatar from "../components/user/Avatar";
import { fDateTime } from "../utils/formatTime";
import { CatalogCard } from "../components/catalog/CatalogCard";
import { CatalogGroup } from "../components/catalog/CatalogGroup";
import { CatalogHeader } from "../components/catalog/CatalogHeader";
import { CatalogFilter } from "../components/catalog/CatalogFilter";
import { WorkoutResumeCard } from "../components/workouts/WorkoutResumeCard";
import { rememberedWorkout } from "../utils/last-workout";
import { groupByTitle } from "../utils/grouping";
import { byTitle } from "../utils/by-title";

const TABLE_HEAD: UserListHeadCell[] = [
    { id: 'date', label: 'Date', alignRight: false },
    { id: 'topic', label: 'Topic', alignRight: false },
    { id: 'users', label: 'Users', alignRight: false }
];

// This table has no interactive sorting, filtering or paging: the sort/filter/page state was
// never updated, and it sorted by a 'name' field that a workout does not have, so the
// comparator always returned 0 and the list kept its server order. These constants record the
// values the table was always rendered with.
const TABLE_ORDER = 'asc';
const TABLE_ORDER_BY = 'name';
const ROWS_SHOWN = 25;

/** The query parameter that opens the catalogue already searched, e.g. from the not found page. */
export const SEARCH_PARAM = 'search';

// A definition with fewer questions than this is still advertised as a preview.
const PREVIEW_QUESTIONS = 100;

export default function WorkoutsPage() {
    const navigate = useNavigate();

    const [ workouts, setWorkouts ] = useState<WorkoutsListEntry[]>([]);
    const [ workoutDefinitions, setWorkoutDefinitions ] = useState<WorkoutDefinition[]>([]);
    const [ unfinished, setUnfinished ] = useState<Workout | null>(null);
    const [ searchParams ] = useSearchParams();
    const [ search, setSearch ] = useState(searchParams.get(SEARCH_PARAM) ?? '');
    const [ group, setGroup ] = useState<string | undefined>(undefined);

    // The backend keeps the definitions of one group together, so reading them in order rebuilds the groups
    const allGroups = groupByTitle(workoutDefinitions, (definition) => definition.group);
    const query = search.trim().toLowerCase();
    const definitionGroups = allGroups
        .filter((candidate) => !group || candidate.title === group)
        .map((candidate) => ({ ...candidate, items: candidate.items.filter((definition) => definition.title.toLowerCase().includes(query)).sort(byTitle) }))
        .filter((candidate) => candidate.items.length);

    useEffect(() => {
        WorkoutService.getWorkouts().then(data => {
            console.log("Workouts history loaded: ", data);
            setWorkouts(data);
        });
    }, []);

    // The last workout the user took part in is offered to resume while they have not finished it
    useEffect(() => {
        const workoutId = rememberedWorkout();
        if (!workoutId) return;

        WorkoutService.getWorkout(workoutId)
            .then((workout) => {
                if (workout.id && workout.currentUser && !workout.currentUser.finished) setUnfinished(workout);
            })
            .catch((reason) => console.error("Unable to load the last workout: ", reason));
    }, []);

    useEffect(() => {
        WorkoutDefinitionsService.workoutDefinitions().then(data => {
            console.log("Workout definitions loaded", data);
            setWorkoutDefinitions(data);
        });
    }, []);

    function startWorkoutOfTopic(definition: WorkoutDefinition) {
        console.log("Start new workout for definition:", definition);
        WorkoutService.startNewWorkout(definition.title, 5, definition.topics, [], definition.tags)
            .then(response => {
                if (!response.id) {
                    alert("Error: Sorry it looks like currently we are not able to start workout for you! Please try again later.");
                    return;
                }

                console.log("Workout started: " + response.id);
                navigate('/workouts/' + response.id);
            })
            .catch(reason => {
                console.error("Unable to start workout: ", reason);
            });
    }

    return <>
        <Helmet>
            <title>Workouts | { ENV.APP_NAME }</title>
        </Helmet>

        <Container maxWidth="xl">
            <Stack spacing={ 4 } sx={ { mb: 6 } }>
                <CatalogHeader title="Workouts"
                               subtitle="Five questions per run. Pick a topic, keep your lives, beat your score."
                               search={ { label: 'Search topics', placeholder: 'Java, SQL, Kafka…', value: search, onChange: setSearch } }/>

                { unfinished && <WorkoutResumeCard workout={ unfinished } cover={ resumeCover(unfinished, workoutDefinitions) }/> }

                <CatalogFilter groups={ allGroups.map((candidate) => candidate.title) } selected={ group }
                               allLabel="All topics" onSelect={ setGroup }/>

                { !definitionGroups.length && workoutDefinitions.length > 0 && (
                    <Typography sx={ { color: '#4F5B67' } }>No topics match “{ search }”.</Typography>
                ) }

                {
                    definitionGroups.map(definitionGroup =>
                        <CatalogGroup key={ definitionGroup.title } title={ definitionGroup.title }
                                      count={ `${ definitionGroup.items.length } ${ definitionGroup.items.length === 1 ? 'workout' : 'workouts' }` }>
                            {
                                definitionGroup.items.map(definition =>
                                    <CatalogCard key={ definition.id }
                                                 item={ {
                                                     cover: `/assets/images/covers/${ definition.id }.svg`,
                                                     title: definition.title,
                                                     size: definition.size,
                                                     rate: definition.rate,
                                                     preview: definition.size < PREVIEW_QUESTIONS
                                                 } }
                                                 onClick={ () => startWorkoutOfTopic(definition) }/>
                                )
                            }
                        </CatalogGroup>
                    )
                }
            </Stack>

            <Typography component="h2" variant="inherit" sx={ { fontSize: 20, lineHeight: '28px', fontWeight: 800, mb: 2 } }>Workouts History</Typography>
            <Card>
                <Scrollbar>
                    <TableContainer sx={ { minWidth: 800 } }>
                        <Table>
                            <UserListHead
                                order={ TABLE_ORDER }
                                orderBy={ TABLE_ORDER_BY }
                                headLabel={ TABLE_HEAD }
                                rowCount={ 0 }
                                numSelected={ 0 }
                            />

                            <TableBody>
                                { workouts.slice(0, ROWS_SHOWN).map((row) => {
                                    const { id, workoutTitle, created, topics, users } = row;

                                    return (
                                        <TableRow hover key={ id } tabIndex={ -1 } role="checkbox">
                                            <TableCell>{ created ? fDateTime(Date.parse(created)) : '' }</TableCell>
                                            <TableCell align="left">
                                                { workoutTitle || topics.map(topic => topic.name || topic.id).join(', ') }
                                            </TableCell>

                                            <TableCell align="left">
                                                { users.map(user =>
                                                    <Avatar key={ user.id } userId={ user.id }
                                                            sx={ { display: 'inline-block' } }/>
                                                ) }
                                            </TableCell>
                                        </TableRow>
                                    );
                                }) }
                            </TableBody>

                            { /* A "Not found" row used to sit here, gated on a search filter that this
                                 page has no UI for, so it could never render. Removed rather than
                                 re-pointed at an empty workout list, which would newly show it. */ }
                        </Table>
                    </TableContainer>
                </Scrollbar>
            </Card>
        </Container>
    </>;
}

/** A topic workout shows its topic's cover; a lesson verification has none of its own and takes the neutral one. */
function resumeCover(workout: Workout, definitions: WorkoutDefinition[]): string {
    const definition = definitions.find((candidate) => candidate.title === workout.workoutTitle);

    return `/assets/images/covers/${ definition?.id ?? 'cs-general' }.svg`;
}
